import React from 'react';
import { Accordion, AccordionItem, Text } from '@pulsarml/avalon-ui';
import { MonitorIcon, TvIcon } from './components/icons';

import { DraggableListComponent } from './components/DraggableListComponent';
import { MetricType } from './components/AddMetricModal/components/MetricParameterEditComponent';
import { ViewType } from './getMetricsByMachineAndViewType';
import { translate } from '@utils/i18n';
import { useSelector } from 'react-redux';

export type MachineMetricsComponentProps = {
  machineMetrics: MachineMetricsType<MetricWithNamedType>;
  metricsCatalogueByViewType: MachineMetricsType<MetricWithNamedType>;
  isViewTypeEnable: (viewType: ViewType) => boolean;
  onChange: (viewType: ViewType, newData: MetricWithNamedType[]) => void;
};

export type MetricWithNamedType = MetricType & {
  metric: string;
};

export type MachineMetricsType<T> = {
  [viewType: string]: T[];
};
export const allViewTypes: ViewType[] = ['desktopNonKpi', 'tvKpi'];
export function MachineMetricsComponent({
  machineMetrics,
  metricsCatalogueByViewType,
  onChange
}: MachineMetricsComponentProps) {
  const configData = useSelector((store: any) => store.config);
  const viewTypesToShow = allViewTypes.filter((viewType) =>
    viewType === 'tvKpi'
      ? configData?.data?.realtime?.singleMachineModeTvVersionEnabled
      : true
  );
  return (
    <Accordion className="tw-w-full" type="multiple">
      {machineMetrics &&
        metricsCatalogueByViewType &&
        viewTypesToShow.map((viewType, index) => {
          return (
            <div key={`${viewType}${index}`}>
              {metricsCatalogueByViewType[viewType] &&
                machineMetrics[viewType] && (
                  <AccordionItem
                    key={`${viewType}${index}`}
                    {...{ value: viewType.toString() }}
                    titleContent={<TitleContent viewType={viewType} />}
                  >
                    <DraggableListComponent
                      maxNumberOfMetrics={viewType === 'tvKpi' ? 6 : undefined}
                      metricsCatalogue={metricsCatalogueByViewType[viewType]
                        .filter(
                          (x: MetricWithNamedType) =>
                            !machineMetrics[viewType]
                              .filter((y) => y.enabled)
                              .find((y) => y.metric === x.metric)
                        )
                        .sort((a, b) => {
                          const indexA = forcedMetricsSort.indexOf(a.metric);
                          const indexB = forcedMetricsSort.indexOf(b.metric);
                          return (
                            (indexA === -1
                              ? forcedMetricsSort.length + a.order
                              : indexA) -
                            (indexB === -1
                              ? forcedMetricsSort.length + b.order
                              : indexB)
                          );
                        })}
                      metrics={machineMetrics[viewType]
                        .filter((y) => y.enabled)
                        .map((metric, index) => ({
                          ...metric,
                          order: index,
                          enabled: true,
                          name: metric.metric,
                          metric: metric.metric
                        }))}
                      onChange={(newData) => onChange(viewType, newData)}
                    />
                  </AccordionItem>
                )}
            </div>
          );
        })}
    </Accordion>
  );
}

const TitleContent = ({ viewType }: { viewType: ViewType }) => {
  const title = translate(`header_machine_config_${viewType}`);
  const icon = viewType === 'desktopNonKpi' ? <MonitorIcon /> : <TvIcon />;
  return (
    <>
      {icon}{' '}
      <Text variant="normal" className="tw-ml-2 tw-mr-auto">
        {' '}
        {title}
      </Text>
    </>
  );
};

const forcedMetricsSort = [
  'availability',
  'netAvailability',
  'instantaneous_velocity',
  'avg_velocity',
  'total',
  'correct',
  'defects',
  'oee_quality',
  'oee_oee',
  'netOee',
  'scrap'
];
